import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Stack,
  SimpleGrid,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { Award, Building2, Briefcase, CirclePlay, ShieldCheck } from 'lucide-react';

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);

const TRUST_BADGES = [
  { icon: Award, label: 'Registered Entity' },
  { icon: Briefcase, label: 'Curated Portfolio' },
  { icon: ShieldCheck, label: 'Secure & Transparent' },
  { icon: Building2, label: 'Asset-Backed Investments' },
];

const HEADING_LINES = [
  ['Invest', 'in', 'Premium'],
  ['Real', 'Estate', 'Opportunities'],
];
const HEADING_ACCENT = ['From', 'Anywhere'];

const HeroSection = () => {
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 1000], [0, 220]);
  const shouldReduceMotion = useReducedMotion();

  const headingContainerVariants = {
    hidden: {},
    visible: {
      transition: shouldReduceMotion
        ? { staggerChildren: 0, delayChildren: 0 }
        : { staggerChildren: 0.05, delayChildren: 0.3 },
    },
  };

  const wordVariants = shouldReduceMotion
    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
    : {
        hidden: { opacity: 0, y: '0.35em' },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
        },
      };

  return (
    <Box
      as="section"
      pt={{ base: '108px', sm: '124px', md: '148px' }}
      pb={{ base: 7, md: 8 }}
      bg="#071326"
      position="relative"
      overflow="hidden"
      minH={{ lg: '560px' }}
    >
      <MotionBox
        position="absolute"
        inset={0}
        bgImage="url('/images/Hero_Image.png')"
        bgSize="cover"
        bgPosition={{ base: 'center', lg: 'right center' }}
        opacity={{ base: 0.95, lg: 0.95 }}
        style={{ y: imageY }}
      />
      <Box
        position="absolute"
        inset={0}
        bg={{
          base: 'linear-gradient(180deg, rgba(7,19,38,0.72) 0%, rgba(7,19,38,0.28) 45%, rgba(7,19,38,0.50) 80%, #071326 100%)',
          lg: 'linear-gradient(90deg, #071326 0%, rgba(7,19,38,0.92) 32%, rgba(7,19,38,0.18) 65%, rgba(7,19,38,0) 100%)',
        }}
      />
      <Box
        position="absolute"
        inset={0}
        bg="linear-gradient(180deg, rgba(7,19,38,0) 0%, #071326 100%)"
      />

      <Container maxW="container.xl" px={{ base: 5, md: 8 }}>
        <MotionBox
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
          position="relative"
          maxW={{ base: 'full', lg: '690px' }}
        >
          <MotionHeading
            as="h1"
            fontFamily="heading"
            fontWeight={500}
            fontSize={{ base: '2.25rem', sm: '2.7rem', md: '4.1rem', xl: '4.8rem' }}
            lineHeight={{ base: 1.02, md: 0.98 }}
            color="#F5F7FA"
            letterSpacing="0"
            variants={headingContainerVariants}
            initial="hidden"
            animate="visible"
          >
            {HEADING_LINES.map((line, lineIdx) => (
              <Box as="span" key={lineIdx} display="block">
                {line.map((word, wordIdx) => (
                  <Box
                    as={motion.span}
                    key={`${lineIdx}-${wordIdx}`}
                    variants={wordVariants}
                    display="inline-block"
                    mr={wordIdx < line.length - 1 ? '0.25em' : 0}
                    sx={{ willChange: 'transform, opacity' }}
                  >
                    {word}
                  </Box>
                ))}
              </Box>
            ))}
            <Box as="span" display="block">
              {HEADING_ACCENT.map((word, idx) => (
                <Box
                  as={motion.span}
                  key={idx}
                  variants={wordVariants}
                  display="inline-block"
                  mr={idx < HEADING_ACCENT.length - 1 ? '0.25em' : 0}
                  sx={{
                    background: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    willChange: 'transform, opacity',
                  }}
                >
                  {word}
                </Box>
              ))}
            </Box>
          </MotionHeading>

          <Text
            mt={5}
            fontFamily="body"
            fontSize={{ base: 'md', md: 'lg' }}
            color="#F5F7FA"
            maxW="560px"
            lineHeight={1.65}
          >
            BlueLagoon &amp; Partners makes institutional-grade real estate
            investing accessible, transparent and secure for everyone.
          </Text>

          <Stack direction="row" spacing={{ base: 2, sm: 4 }} mt={8} align="stretch">
            <Button
              as={RouterLink}
              to="/investments"
              variant="primary"
              h="46px"
              px={{ base: 3, sm: 8 }}
              fontSize={{ base: 'xs', sm: 'sm' }}
              flex={{ base: '1', sm: '0 0 auto' }}
            >
              Explore Opportunities
            </Button>
            <Button
              as={RouterLink}
              to="/how-it-works"
              variant="outline"
              h="46px"
              px={{ base: 3, sm: 7 }}
              fontSize={{ base: 'xs', sm: 'sm' }}
              leftIcon={<CirclePlay size={17} />}
              flex={{ base: '1', sm: '0 0 auto' }}
            >
              How It Works
            </Button>
          </Stack>

          <SimpleGrid
            columns={{ base: 2, md: 4 }}
            spacing={0}
            mt={8}
            border="1px solid"
            borderColor="rgba(255,255,255,0.14)"
            borderRadius="8px"
            bg="rgba(10,27,52,0.52)"
            backdropFilter="blur(8px)"
            overflow="hidden"
            maxW="740px"
          >
            {TRUST_BADGES.map((item, i) => (
              <Flex
                key={item.label}
                align="center"
                gap={3}
                px={{ base: 3, md: 5 }}
                py={3}
                borderRight={{
                  base: i % 2 === 0 ? '1px solid rgba(255,255,255,0.14)' : 'none',
                  md: i < TRUST_BADGES.length - 1 ? '1px solid rgba(255,255,255,0.14)' : 'none',
                }}
                borderBottom={{
                  base: i < 2 ? '1px solid rgba(255,255,255,0.10)' : 'none',
                  md: 'none',
                }}
              >
                <Icon as={item.icon} color="#D4A85F" boxSize={4} strokeWidth={1.7} flexShrink={0} />
                <Text color="#F5F7FA" fontSize="xs">
                  {item.label}
                </Text>
              </Flex>
            ))}
          </SimpleGrid>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default HeroSection;
